import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cardHasPrintedPT } from '@mtg-deck-tool/shared';
import { usePlaytesterStore } from '../../stores/playtester-store.js';

interface MenuItemProps {
  label: string;
  onClick: () => void;
  checked?: boolean;
  disabled?: boolean;
}

function MenuItem({ label, onClick, checked, disabled }: MenuItemProps) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={`w-full text-left px-3 py-1.5 text-sm rounded transition flex items-center gap-2 ${
        disabled
          ? 'text-gray-600 cursor-not-allowed'
          : 'text-gray-200 hover:bg-gray-700 hover:text-white'
      }`}
    >
      {checked !== undefined && (
        <span className="w-4 shrink-0 text-orange-500 font-bold text-xs">
          {checked ? '✓' : ''}
        </span>
      )}
      {label}
    </button>
  );
}

function MenuDivider() {
  return <div className="border-t border-gray-700 my-0.5" />;
}

/**
 * Hook that clamps a menu container to viewport bounds after render.
 * Returns a style object with the clamped position.
 */
function useClampedMenuPos(
  ref: React.RefObject<HTMLDivElement>,
  rawX: number,
  rawY: number,
): React.CSSProperties {
  const [style, setStyle] = useState<React.CSSProperties>({ left: rawX, top: rawY, visibility: 'hidden' });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = rawX;
    let top = rawY;
    if (left + rect.width > vw - 4) left = vw - rect.width - 4;
    if (top + rect.height > vh - 4) top = vh - rect.height - 4;
    if (left < 4) left = 4;
    if (top < 4) top = 4;
    setStyle({ left, top, visibility: 'visible' });
  }, [rawX, rawY]);

  return style;
}

export default function ContextMenu() {
  const contextMenu = usePlaytesterStore((s) => s.contextMenu);
  const setContextMenu = usePlaytesterStore((s) => s.setContextMenu);
  const moveToZone = usePlaytesterStore((s) => s.moveToZone);
  const moveToTopOfLibrary = usePlaytesterStore((s) => s.moveToTopOfLibrary);
  const moveToBottomOfLibrary = usePlaytesterStore((s) => s.moveToBottomOfLibrary);
  const moveToLibraryPosition = usePlaytesterStore((s) => s.moveToLibraryPosition);
  const tapCard = usePlaytesterStore((s) => s.tapCard);
  const flipCard = usePlaytesterStore((s) => s.flipCard);
  const setDoesntUntap = usePlaytesterStore((s) => s.setDoesntUntap);
  const makeTokenCopy = usePlaytesterStore((s) => s.makeTokenCopy);
  const returnToCommandZone = usePlaytesterStore((s) => s.returnToCommandZone);
  const openCounterModal = usePlaytesterStore((s) => s.openCounterModal);
  const openPTModal = usePlaytesterStore((s) => s.openPTModal);
  const addGenericCounter = usePlaytesterStore((s) => s.addGenericCounter);
  const removeGenericCounter = usePlaytesterStore((s) => s.removeGenericCounter);
  const resetCounters = usePlaytesterStore((s) => s.resetCounters);
  const resetPowerToughness = usePlaytesterStore((s) => s.resetPowerToughness);
  const battlefield = usePlaytesterStore((s) => s.battlefield);

  const containerRef = useRef<HTMLDivElement>(null);
  const [libraryPositionInput, setLibraryPositionInput] = useState('1');
  const [showLibraryPositionInput, setShowLibraryPositionInput] = useState(false);

  const rawX = contextMenu?.x ?? 0;
  const rawY = contextMenu?.y ?? 0;
  const clampedStyle = useClampedMenuPos(containerRef as React.RefObject<HTMLDivElement>, rawX, rawY);

  // Dismiss on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setContextMenu(null);
        setShowLibraryPositionInput(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [setContextMenu]);

  if (!contextMenu) return null;

  const { instanceId, zone } = contextMenu;

  function dismiss() {
    setContextMenu(null);
    setShowLibraryPositionInput(false);
  }

  // Find the card instance on the battlefield (commanders stay here after being cast)
  const bfInstance = battlefield.find((i) => i.instanceId === instanceId);
  const card = bfInstance?.card;

  // isCommander is tracked on the instance itself so it survives zone changes
  const isCommander = bfInstance?.isCommander ?? false;

  // Is doesntUntap set for this card?
  const doesntUntap = bfInstance?.doesntUntap ?? false;

  // Hand menu
  if (zone === 'hand') {
    return (
      <div
        ref={containerRef}
        className="fixed z-[200] bg-gray-900 border border-gray-700 rounded-lg shadow-2xl py-1 min-w-[200px]"
        style={clampedStyle}
      >
        <MenuItem
          label="Move to Battlefield"
          onClick={() => { moveToZone([instanceId], 'hand', 'battlefield'); dismiss(); }}
        />
        <MenuItem
          label="Move to Battlefield Tapped"
          onClick={() => {
            moveToZone([instanceId], 'hand', 'battlefield');
            tapCard(instanceId);
            dismiss();
          }}
        />
        <MenuItem
          label="Move to Battlefield Flipped"
          onClick={() => {
            moveToZone([instanceId], 'hand', 'battlefield');
            flipCard(instanceId);
            dismiss();
          }}
        />
        <MenuDivider />
        <MenuItem
          label="Move to Top of Library"
          onClick={() => { moveToTopOfLibrary([instanceId]); dismiss(); }}
        />
        {showLibraryPositionInput ? (
          <div className="px-3 py-1.5 flex items-center gap-1.5">
            <span className="text-xs text-gray-400">Position:</span>
            <input
              type="number"
              min={1}
              value={libraryPositionInput}
              onChange={(e) => setLibraryPositionInput(e.target.value)}
              className="w-12 text-center text-xs bg-gray-800 border border-gray-600 rounded text-white outline-none"
              autoFocus
            />
            <button
              onClick={() => {
                const pos = Math.max(0, parseInt(libraryPositionInput, 10) - 1) || 0;
                moveToLibraryPosition(instanceId, pos);
                dismiss();
              }}
              className="text-xs px-1.5 py-0.5 bg-orange-600 hover:bg-orange-700 text-white rounded transition"
            >
              OK
            </button>
          </div>
        ) : (
          <MenuItem
            label="Move to X from Top of Library"
            onClick={() => setShowLibraryPositionInput(true)}
          />
        )}
        <MenuItem
          label="Move to Bottom of Library"
          onClick={() => { moveToBottomOfLibrary([instanceId]); dismiss(); }}
        />
        <MenuDivider />
        <MenuItem
          label="Move to Graveyard"
          onClick={() => { moveToZone([instanceId], 'hand', 'graveyard'); dismiss(); }}
        />
        <MenuItem
          label="Move to Exile"
          onClick={() => { moveToZone([instanceId], 'hand', 'exile'); dismiss(); }}
        />
      </div>
    );
  }

  // Battlefield menu
  return (
    <div
      ref={containerRef}
      className="fixed z-[200] bg-gray-900 border border-gray-700 rounded-lg shadow-2xl py-1 min-w-[220px]"
      style={clampedStyle}
    >
      <MenuItem
        label={bfInstance?.tapped ? 'Untap' : 'Tap'}
        onClick={() => { tapCard(instanceId); dismiss(); }}
      />
      <MenuItem
        label="Turn Over (Flip)"
        onClick={() => { flipCard(instanceId); dismiss(); }}
      />
      <MenuItem
        label="Doesn't Untap"
        checked={doesntUntap}
        onClick={() => { setDoesntUntap(instanceId, !doesntUntap); dismiss(); }}
      />
      <MenuDivider />
      <MenuItem
        label="Add a Counter"
        onClick={() => { addGenericCounter(instanceId); dismiss(); }}
      />
      <MenuItem
        label="Remove a Counter"
        onClick={() => { removeGenericCounter(instanceId); dismiss(); }}
      />
      <MenuItem
        label="Set Counters"
        onClick={() => { openCounterModal(); setContextMenu({ x: rawX, y: rawY, instanceId, zone }); }}
      />
      <MenuItem
        label="Reset Counters"
        onClick={() => { resetCounters(instanceId); dismiss(); }}
      />
      {card && cardHasPrintedPT(card) && (
        <>
          <MenuDivider />
          <MenuItem
            label="Set Power / Toughness"
            onClick={() => { openPTModal(); setContextMenu({ x: rawX, y: rawY, instanceId, zone }); }}
          />
          <MenuItem
            label="Reset Power / Toughness"
            onClick={() => { resetPowerToughness(instanceId); dismiss(); }}
          />
        </>
      )}
      <MenuDivider />
      {card && (
        <MenuItem
          label="Make Token Copy"
          onClick={() => { makeTokenCopy(instanceId); dismiss(); }}
        />
      )}
      <MenuDivider />
      <MenuItem
        label="Move to Hand"
        onClick={() => { moveToZone([instanceId], 'battlefield', 'hand'); dismiss(); }}
      />
      <MenuItem
        label="Move to Graveyard"
        onClick={() => { moveToZone([instanceId], 'battlefield', 'graveyard'); dismiss(); }}
      />
      <MenuItem
        label="Move to Exile"
        onClick={() => { moveToZone([instanceId], 'battlefield', 'exile'); dismiss(); }}
      />
      <MenuItem
        label="Move to Top of Library"
        onClick={() => { moveToTopOfLibrary([instanceId]); dismiss(); }}
      />
      <MenuItem
        label="Move to Bottom of Library"
        onClick={() => { moveToBottomOfLibrary([instanceId]); dismiss(); }}
      />
      {isCommander && (
        <>
          <MenuDivider />
          <MenuItem
            label="Move to Command Zone"
            onClick={() => { returnToCommandZone(instanceId); dismiss(); }}
          />
        </>
      )}
    </div>
  );
}
